import React, { useState, useEffect, useCallback } from 'react';
import StaticData from './StaticData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrash,
  faMagnifyingGlass,
  // faGripVertical,
  faPenToSquare,
} from '@fortawesome/free-solid-svg-icons';

import { useMutation } from '@tanstack/react-query';
import { classNames, createTerm, createNextSpringTerm } from 'utils';
// import OutsideClickHandler from 'react-outside-click-handler';

import CourseList from './RequirementCourseList';
import Elective from './RequirementElective';
import Text from './RequirementText';
import GeneralStudy from './RequirementGeneralStudy';
import Check from './RequirementCheck';
import { useSelector } from 'react-redux';

// Sort course topics by description, ignoring any leading whitespace in the
// source data. Some catalog topics arrive with a stray leading space, which
// would otherwise sort them ahead of everything else (a space sorts before
// letters). Internal spaces are intentionally preserved.
const compareTopicsByDescription = (a, b) =>
  (a.description ?? '')
    .replace(/^\s+/, '')
    .localeCompare((b.description ?? '').replace(/^\s+/, ''));

//pass the createWrappedData function to the child, pass dataFromParent to child, call back the dataToParent to parent
// modal=true, display modal, otherwise display as text
const Course = ({
  index,
  data,
  handleInputChange,
  handleDeleteComponent,
  showCourseListOption,
  isCourseListItem = false,
  isRecommendedCourse = false,
  handleShowAsCourseList = null,
}) => {
  const [topics, setTopics] = useState([]);
  const [showTopicsDropdown, setShowTopicsDropdown] = useState(false);
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [customHours, setCustomHours] = useState(
    isCourseListItem &&
      (data.hours !== data.rangeHours || data.maxHours !== data.rangeMaxHours)
      ? 'custom'
      : ''
  );

  const subjectList = useSelector((state) => state.dataLists.subjectList);

  const {
    mutate: fetchCourse,
    data: fetchCourseData,
    error: fetchCourseError,
    isSuccess: fetchCourseIsSuccess,
    isPending: fetchCourseIsLoading,
  } = useMutation({
    mutationFn: async ({ subject, catalogNumber }) => {
      let currentFallTerm = '2257';
      let nextSpringTerm = '2261';

      const params = new URLSearchParams(window.location.search);
      const checkId = params.get('id');

      // Extract the first 4 characters as the year
      const year =
        checkId && checkId.length >= 4 ? checkId.substring(0, 4) : null;

      if (year) {
        currentFallTerm = createTerm(year);
        nextSpringTerm = createNextSpringTerm(year);
      }

      const mkUrl = (term) =>
        `https://api.myasuplat-dpl.asu.edu/api/course/${term}/subject/${subject}/${catalogNumber}`;

      const urls = [mkUrl(currentFallTerm), mkUrl(nextSpringTerm)];

      // fetch both in parallel
      const results = await Promise.all(
        urls.map(async (u) => {
          const res = await fetch(u);
          if (!res.ok) return []; // skip if 404
          return res.json();
        })
      );

      // results is [currentCoursesArray, nextCoursesArray]
      const allCourses = [...(results[0] || []), ...(results[1] || [])];

      // if (!allCourses.length) {
      //   throw new Error(`No courses found for ${subject} ${catalogNumber}`);
      // }

      return allCourses;
    },
  });

  // Callback ref to focus input only for new/empty courses on mount
  // This prevents auto-scrolling issues while still providing good UX for new entries
  const inputRef = useCallback((node) => {
    if (node && !data.subject && !data.number) {
      node.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps intentional - only check initial values on mount, avoid refocus on data changes

  const handleConnectorChange = (e) => {
    const { value } = e.target;

    handleInputChange(index, 'connector', value);
  };

  const clearRequirement = () => {
    handleInputChange(index, 'description', '');
    handleInputChange(index, 'gs', '');
    handleInputChange(index, 'hours', null);
    handleInputChange(index, 'maxHours', null);
    handleInputChange(index, 'topic', null);
    handleInputChange(index, 'courseId', '');
  };

  const handleRequirementTypeChange = (e) => {
    const { value } = e.target;

    handleInputChange(index, 'itemType', value);
    handleInputChange(index, 'subject', '');
    handleInputChange(index, 'number', '');
    clearRequirement();
  };

  const handleSubjectInputChange = (e) => {
    const { value } = e.target;

    clearRequirement();

    if (
      value.length === 3 &&
      data.number &&
      data.number?.length >= 2 &&
      data.number?.length <= 9
    )
      fetchCourse({
        subject: value.toUpperCase(),
        catalogNumber: data.number,
      });

    handleInputChange(index, 'subject', value.toUpperCase());
    setShowSubjectDropdown(true);
  };

  const handleSelectCourseSubject = (subject) => {
    setShowTopicsDropdown(false);
    handleInputChange(index, 'topic', null);

    if (data.number && data.number?.length >= 2 && data.number?.length <= 9) {
      fetchCourse({
        subject: subject.CODE,
        catalogNumber: data.number,
      });
    }

    handleInputChange(index, 'subject', subject.CODE);
    setShowSubjectDropdown(false);
  };

  const handleNumberInputChange = (e) => {
    const { value } = e.target;

    clearRequirement();
    setShowTopicsDropdown(false);
    handleInputChange(index, 'topic', null);

    if (
      value.length >= 2 &&
      value.length <= 9 &&
      data.subject &&
      data.subject?.length === 3
    )
      fetchCourse({
        subject: data.subject,
        catalogNumber: value,
      });

    handleInputChange(index, 'number', value);
  };

  // Sort topics by description
  const handleEditTopic = () => {
    if (data.number?.length === 3 && data.subject && data.subject?.length === 3)
      fetchCourse({
        subject: data.subject,
        catalogNumber: data.number,
      });

    setShowTopicsDropdown(true);
  };

  const handleTopicChange = (e) => {
    const { value } = e.target;
    const sortedTopics = topics && [...topics].sort(compareTopicsByDescription);

    if (value === data.description) {
      // this is the main course
      handleInputChange(index, 'topic', {});
    } else {
      const selected = sortedTopics.find(
        (topic) => topic.description === value
      );

      handleInputChange(index, 'topic', selected);
    }
  };

  const handleCustomHoursChange = (e) => {
    const { valueAsNumber } = e.target;

    if (
      isNaN(valueAsNumber) ||
      valueAsNumber === '' ||
      valueAsNumber < data.rangeHours
    ) {
      handleInputChange(index, 'hours', 0);
    } else if (valueAsNumber > data.rangeMaxHours) {
      handleInputChange(index, 'hours', data.rangeMaxHours);
    } else {
      handleInputChange(index, 'hours', valueAsNumber);
    }

    // A single custom value has no range maximum. Use null ("unset") rather
    // than 0 so an absent maximum is never confused with a true-zero value.
    handleInputChange(index, 'maxHours', null);
  };

  const handleSetCustomHours = (e) => {
    const { value } = e.target;
    setCustomHours(value);

    handleInputChange(index, 'hours', data.rangeHours);

    if (value === 'custom') {
      // "Set credits" = a single value, no range maximum. Use null ("unset").
      handleInputChange(index, 'maxHours', null);
    } else {
      handleInputChange(index, 'maxHours', data.rangeMaxHours);
    }
  };

  const handleProposedTitleChange = (e) => {
    const { value } = e.target;

    handleInputChange(index, 'proposedTitle', value);
  };

  const handleProposedHoursChange = (e) => {
    const { value } = e.target;

    // Keep empty as empty; otherwise clamp to 0 and above
    let nextValue = value;
    if (value !== '') {
      const num = Number(value);
      nextValue = isNaN(num) || num < 0 ? 0 : num;
    }

    handleInputChange(index, 'proposedHours', nextValue);

    // Proposed course: DPL-derived hours fields must be null (mutually exclusive)
    handleInputChange(index, 'hours', null);
    handleInputChange(index, 'maxHours', null);
    handleInputChange(index, 'rangeHours', null);
    handleInputChange(index, 'rangeMaxHours', null);
  };

  // Handle prop updates directly
  // useEffect(() => {
  //   // if (data?.subject) setCourseSubject(data.subject);
  //   // if (data?.number) setCourseNumber(data.number);
  //   // if (data?.gs) setCourseGs(data.gs);
  //   // if (data?.hours) setCourseHours(data.hours);
  //   // if (data?.maxHours) setCourseMaxHours(data.maxHours);
  //   // if (data?.description) setCourseDescription(data.description);
  //   // if (data?.connector) setConnector(data.connector);
  //   // if (data?.topic) setCourseTopic(data.topic);
  //   // if (data?.itemType) setItemType(data.itemType);
  // }, [data]);

  useEffect(() => {
    if (
      data.subject?.length === 3 &&
      data.number?.length >= 2 &&
      data.number?.length <= 9
    ) {
      if (fetchCourseIsSuccess) {
        handleInputChange(index, 'gs', '');
        handleInputChange(index, 'description', '');
        handleInputChange(index, 'courseId', '');
        console.log('fetchCourseData: ' + JSON.stringify(fetchCourseData));

        if (fetchCourseData.length > 0) {
          setTopics(fetchCourseData[0].topics);
          handleInputChange(
            index,
            'description',
            fetchCourseData[0].description
          );
          handleInputChange(index, 'proposedTitle', '');
          // Non-proposed course: proposedHours must be null (mutually exclusive)
          handleInputChange(index, 'proposedHours', null);

          if (fetchCourseData[0].topics) {
            setShowTopicsDropdown(true);
          } else {
            handleInputChange(index, 'hours', null);
            handleInputChange(index, 'maxHours', null);
          }

          handleInputChange(
            index,
            'description',
            fetchCourseData[0].description
          );

          if (fetchCourseData[0].requirementDesignationDescription) {
            var fetchCourseGs =
              fetchCourseData[0].requirementDesignationDescription.split(
                ' '
              )[0];

            if (fetchCourseGs.length !== 4) {
              fetchCourseGs = '';
            }

            handleInputChange(index, 'gs', fetchCourseGs);
          }

          const course = fetchCourseData[0];
          const {
            approvedUnitsMinimum: minUnits,
            approvedUnitsMaximum: maxUnits,
          } = course;

          if (minUnits !== undefined && minUnits !== null) {
            handleInputChange(index, 'hours', minUnits);
          }

          if (maxUnits !== undefined && maxUnits !== null) {
            handleInputChange(index, 'maxHours', maxUnits);
          }

          if (isCourseListItem && ((minUnits !== undefined && minUnits !== null) || (maxUnits !== undefined && maxUnits !== null))) {
            if (minUnits === maxUnits) {
              setCustomHours('');
              handleInputChange(index, 'rangeHours', null);
              handleInputChange(index, 'rangeMaxHours', null);
            } else {
              if (!!data.rangeHours && !!data.rangeMaxHours) {
                if (
                  minUnits !== data.rangeHours &&
                  maxUnits !== data.rangeMaxHours
                ) {
                  setCustomHours('custom');
                }
              } else {
                handleInputChange(index, 'rangeHours', minUnits);
                handleInputChange(index, 'rangeMaxHours', maxUnits);
              }
            }
          }

          if (fetchCourseData[0].courseId) {
            handleInputChange(index, 'courseId', fetchCourseData[0].courseId);
          }
        } else {
          handleInputChange(index, 'description', 'course not found');
        }
      }

      if (fetchCourseError) {
        console.error('Error fetching data:', fetchCourseError.message);
      }
    } else {
      handleInputChange(index, 'description', '');
      handleInputChange(index, 'gs', '');
      handleInputChange(index, 'hours', null);
      handleInputChange(index, 'maxHours', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCourseIsSuccess, fetchCourseData]);

  useEffect(() => {
    let filteredSubjects;

    if (data.subject && !!subjectList.length)
      filteredSubjects = subjectList.filter((subject) =>
        subject.CODE.includes(data.subject.toUpperCase())
      );

    if (!data.subject || !filteredSubjects?.length)
      setShowSubjectDropdown(false);
  }, [data.subject, subjectList]);

  return (
    <>
      {data.itemType === 'req_course' || !data.itemType ? (
        <>
          {isCourseListItem && data.connector && (
            <div style={{ width: '80px' }} className="mx-5 pb-2">
              <select
                className="text-gray-7 p-1 w-100"
                value={data.connector ?? ''}
                name="connector"
                onChange={(e) => handleConnectorChange(e)}
                disabled={
                  index === 0 &&
                  (!data.connector || data.connector.trim() === '')
                }
              >
                {StaticData.connectors.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    className={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="d-flex gap-2 pb-2">
            {/* Remove until reordering is finished */}
            {/* {!isCourseListItem && (
              <FontAwesomeIcon
                icon={faGripVertical}
                style={{ width: '15px' }}
                className="pt-4"
              />
            )} */}
            <div className="mt-4 me-1">
              <FontAwesomeIcon
                icon={faTrash}
                onClick={() => handleDeleteComponent(index)}
                className={`deleteButton ${showCourseListOption ? 'fa-trash-active' : ''}`}
              />
            </div>

            {!isRecommendedCourse && !isCourseListItem && (
              <div style={{ width: '132px' }}>
                <div className="fw-bold text-nowrap">Logical operator</div>
                <select
                  className="text-gray-7 p-1 w-100"
                  value={data.connector ?? ''}
                  name="connector"
                  onChange={(e) => handleConnectorChange(e)}
                  disabled={
                    index === 0 &&
                    (!data.connector || data.connector.trim() === '')
                  }
                >
                  {StaticData.connectors.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      className={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!isRecommendedCourse && (
              <div>
                <div className="fw-bold">Requirement type</div>
                <div>
                  <select
                    style={{
                      width: '150px',
                    }}
                    className="text-gray-7 p-1"
                    value={'req_course'}
                    onChange={(e) => handleRequirementTypeChange(e)}
                  >
                    {showCourseListOption
                      ? StaticData.reqList.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))
                      : isCourseListItem
                        ? StaticData.courseListReqList.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))
                        : StaticData.templateReqList.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                  </select>
                </div>
              </div>
            )}

            <div>
              <div className="fw-bold">Subject</div>
              {/* <OutsideClickHandler
                onOutsideClick={() => setShowSubjectDropdown(false)}
              > */}
              <div className="position-relative">
                <input
                  type="text"
                  name="subject"
                  value={data.subject ?? ''}
                  onChange={(e) => handleSubjectInputChange(e)}
                  onBlur={() =>
                    setTimeout(() => setShowSubjectDropdown(false), 200)
                  }
                  className="p-1"
                  style={{ width: '110px' }}
                  autoComplete="off"
                  maxLength={3}
                  placeholder="Subject"
                  ref={inputRef} // Attach the ref to the input field
                />
                {data.subject?.length === 0 && (
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    style={{
                      position: 'absolute',
                      color: 'gray',
                      left: '70px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  />
                )}
                {showSubjectDropdown && (
                  <div
                    className="position-absolute border border-gray-5 overflow-y-auto"
                    style={{
                      maxHeight: '170px',
                      width: '350px',
                      zIndex: 1,
                    }}
                  >
                    {subjectList
                      .filter((subject) =>
                        subject.CODE.includes(data.subject.toUpperCase())
                      )
                      .map((subject, index, array) => (
                        <div
                          key={subject.CODE}
                          className={classNames(
                            index !== array.length - 1 && 'border-bottom',
                            'border-gray-5 p-2 bg-white d-flex gap-2'
                          )}
                          onClick={() => handleSelectCourseSubject(subject)}
                          role="button"
                        >
                          <div className="fs-6 fw-bold">{subject.CODE}</div>
                          <div>{subject.DESCR}</div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
              {/* </OutsideClickHandler> */}
            </div>

            <div>
              <div className="fw-bold">Number</div>
              <input
                className="p-1"
                style={{ width: '110px' }}
                type="text"
                name="number"
                value={data.number ?? ''}
                placeholder="Number"
                onChange={(e) => handleNumberInputChange(e)}
              />
            </div>

            {showTopicsDropdown ? (
              <div>
                <div className="fw-bold">Course title</div>
                <div className="d-flex gap-2 align-items-center">
                  {fetchCourseIsLoading ? (
                    <div>Loading...</div>
                  ) : (
                    <select
                      value={
                        (data.topic?.description || data.description) ?? ''
                      }
                      style={{ width: '350px' }}
                      onChange={(e) => handleTopicChange(e)}
                    >
                      <option value="" disabled>
                        Select title
                      </option>
                      {/* Add a default option for the main course */}
                      <option value={data.description}>
                        {data.description}
                      </option>
                      {topics &&
                        topics
                          .toSorted(compareTopicsByDescription)
                          .map((topic, index) => (
                            <option key={index} value={topic.description}>
                              {topic.description}
                            </option>
                          ))}
                    </select>
                  )}
                  {!isCourseListItem && (
                    <div>
                      {!!data.gs && <span> ({data.gs})</span>}
                      {(data.hours !== null && data.hours !== undefined) && ` (${data.hours} `}
                      {data.maxHours !== null &&
                        data.maxHours !== undefined &&
                        data.maxHours !== 0 &&
                        data.maxHours !== data.hours &&
                        `${' - '} ${data.maxHours}`}{' '}
                      {(data.hours !== null && data.hours !== undefined) && ` credit hours) `}
                    </div>
                  )}
                </div>
              </div>
            ) : data.proposedTitle ||
              data.description === 'course not found' ? (
              <div className="d-flex gap-2">
                <div>
                  <div className="fw-bold">Course title (proposed)</div>
                  <input
                    type="text"
                    placeholder="Course title"
                    value={data.proposedTitle ?? ''}
                    onChange={handleProposedTitleChange}
                    style={{ width: '400px' }}
                  />
                </div>
                <div style={{ width: '100px' }}>
                  <div className="fw-bold">Credit hours</div>
                  <input
                    type="number"
                    placeholder="#"
                    min={0}
                    step={1}
                    value={data.proposedHours ?? ''}
                    onChange={handleProposedHoursChange}
                    onKeyDown={(e) => {
                      if (['-', '+', 'e', 'E'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    className="text-end"
                    style={{ width: '100px' }}
                  />
                </div>
              </div>
            ) : (
              (!!data.topic || !!data.description) && (
                <div>
                  <div className="fw-bold">Course title</div>
                  <div style={{ paddingTop: '4px' }}>
                    {data.topic?.description ? (
                      <span
                        className="text-maroon underline-hover"
                        onClick={handleEditTopic}
                        role="button"
                      >
                        <FontAwesomeIcon
                          icon={faPenToSquare}
                          className="me-1"
                        />
                        {data.topic.description}
                      </span>
                    ) : (
                      <span>{data.description}</span>
                    )}
                    {(!isCourseListItem ||
                      !data.rangeMaxHours ||
                      data.rangeMaxHours <= data.rangeHours) && (
                      <>
                        {!!data.gs && <span> ({data.gs})</span>}
                        {(data.hours !== null && data.hours !== undefined) && ` (${data.hours} `}
                        {data.maxHours !== null &&
                          data.maxHours !== undefined &&
                          data.maxHours !== 0 &&
                          data.maxHours !== data.hours &&
                          `${' - '} ${data.maxHours}`}{' '}
                        {(data.hours !== null && data.hours !== undefined) && ` credit hours) `}
                      </>
                    )}
                  </div>
                </div>
              )
            )}
            {isCourseListItem &&
              !!data.rangeMaxHours &&
              data.rangeMaxHours > data.rangeHours && (
                <div>
                  <div className="fw-bold text-nowrap">Credit hours</div>
                  <div className="d-flex gap-2">
                    <select
                      value={customHours}
                      onChange={(e) => handleSetCustomHours(e)}
                    >
                      <option value="">
                        {data.rangeHours}-{data.rangeMaxHours}
                      </option>
                      <option value="custom">Set credits</option>
                    </select>
                    {customHours === 'custom' && (
                      <input
                        className="p-1"
                        style={{ width: '60px' }}
                        name="topic-hours"
                        type="number"
                        value={!!data.hours ? data.hours : ''}
                        placeholder="#"
                        min={data.rangeHours}
                        max={data.rangeMaxHours}
                        onChange={(e) => handleCustomHoursChange(e)}
                        onKeyDown={(e) => {
                          if (['-', '+', 'e', 'E'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
          </div>
        </>
      ) : data.itemType === 'req_course_list' ? (
        <CourseList
          index={index}
          data={data}
          handleInputChange={handleInputChange}
          handleDeleteComponent={handleDeleteComponent}
          handleShowAsCourseList={handleShowAsCourseList}
        />
      ) : data.itemType === 'req_elective' ? (
        <Elective
          index={index}
          data={data}
          handleInputChange={handleInputChange}
          handleDeleteComponent={handleDeleteComponent}
          showCourseListOption={showCourseListOption}
          isCourseListItem={isCourseListItem}
        />
      ) : data.itemType === 'req_gs' ? (
        <GeneralStudy
          index={index}
          data={data}
          handleInputChange={handleInputChange}
          handleDeleteComponent={handleDeleteComponent}
          showCourseListOption={showCourseListOption}
          isCourseListItem={isCourseListItem}
        />
      ) : data.itemType === 'req_text' ? (
        <Text
          index={index}
          data={data}
          handleInputChange={handleInputChange}
          handleDeleteComponent={handleDeleteComponent}
          showCourseListOption={showCourseListOption}
          isCourseListItem={isCourseListItem}
        />
      ) : data.itemType === 'req_check' ? (
        <Check
          index={index}
          data={data}
          handleInputChange={handleInputChange}
          handleDeleteComponent={handleDeleteComponent}
          showCourseListOption={showCourseListOption}
          isCourseListItem={isCourseListItem}
        />
      ) : (
        <div>{/* Handle other cases if needed */}</div>
      )}
    </>
  );
};

export default Course;
